import React, { useEffect, useState } from "react";
import { custom, logger } from "@origin-digital/reporting-client";
import {
  removePlanFuel,
  removePlanSolar,
  removeSearchPostcode,
  removeConnectionScenario,
  useUserContext,
} from "@origin-digital/sales-common";
import { fetchUserProfile } from "@origin-digital/user-profile";
import { Box, Preloader } from "@origin-digital/ods-core";

import { config } from "src/config";
import { CONTENT, LEGACY_MOVE_CONTENT } from "./consts";
import { Lookup } from "./variants/Move/Lookup";
import { LegacyLookup } from "./variants/Move/Lookup/LegacyLookup";
import { isAuthenticated } from "./utils/utils";
import {
  getLegacySalesButtonStatus,
  getSalesButtonStatus,
} from "./utils/salesButtonStatus";
import { useAbTest } from "@origin-digital/optimizely";

const Loading = () => (
  <Box padding="large" display="flex" justifyContent="center">
    <Preloader />
  </Box>
);

const Move: React.FC<{ upliftEnabled: boolean }> = ({ upliftEnabled }) => {
  const { setUserProfile } = useUserContext();
  const [appIsLoading, setAppIsLoading] = useState(true);
  const [isCustomer, setIsCustomer] = useState(false);
  const [hasCustomerProfile, setHasCustomerProfile] = useState(false);

  useEffect(() => {
    async function initialise() {
      const authenticated = await isAuthenticated();
      let profileLoaded = false;
      if (authenticated) {
        try {
          const profile = await fetchUserProfile(config.userProfileApiEndPoint);
          if (profile) {
            profileLoaded = true;
            setUserProfile(profile.krakenUserProfile);
          }
        } catch (error) {
          // Keep the journey available if the profile service fails.
          logger.error("Unable to fetch movers customer profile", error);
        }
      }
      const customer = upliftEnabled
        ? await getSalesButtonStatus(authenticated)
        : profileLoaded || (await getLegacySalesButtonStatus(authenticated));
      setIsCustomer(customer);
      setHasCustomerProfile(profileLoaded);
      custom("movers-widget-wp", "user-type-detection", {
        userType: customer ? "customer" : "prospect",
      });
    }

    removeSearchPostcode();
    removePlanFuel();
    removePlanSolar();
    removeConnectionScenario();
    initialise().finally(() => setAppIsLoading(false));
  }, [setUserProfile, upliftEnabled]);

  if (appIsLoading) {
    return <Loading />;
  }

  if (!upliftEnabled) {
    return (
      <LegacyLookup
        buttonLabelCP={
          hasCustomerProfile ? null : LEGACY_MOVE_CONTENT.buttonLabelCP
        }
        buttonLabelSales={
          isCustomer ? LEGACY_MOVE_CONTENT.buttonLabelSales : null
        }
        selectFuelCheckboxes={LEGACY_MOVE_CONTENT.selectFuel}
      />
    );
  }

  return (
    <Lookup
      isCustomer={isCustomer}
      ctaLabel={
        isCustomer ? CONTENT.move.buttonLabelSales! : CONTENT.move.buttonLabelCP
      }
    />
  );
};

export const Root: React.FC = () => {
  const { loading, variation } = useAbTest(
    config?.optimizelyExperiment?.ENG2995
  ) || { loading: true };

  if (loading) return <Loading />;

  const upliftEnabled = variation === "Exp B";

  return <Move upliftEnabled={upliftEnabled} />;
};
